import React, { useState } from "react";
import { Button, TextField, Box, Typography, CircularProgress } from "@mui/material";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Link as LinkIcon, VpnKey as KeyIcon } from "@mui/icons-material";
import ActionModal from "./modals/action-modal";
import useMutation from "src/hooks/useMutation";
import useNotification from "src/hooks/notification";
import {
  GENERATE_INVITATION_LINK_MUTATION,
  GENERATE_PASSWORD_RESET_LINK_MUTATION,
} from "src/constants/graphql";
import Grid from "@mui/material/Grid";

const LINK_CONFIGS = {
  invitation: {
    title: "Generate Invitation Link",
    buttonText: "Generate Link",
    fieldLabel: "Invitation Link",
    icon: LinkIcon,
    mutation: GENERATE_INVITATION_LINK_MUTATION,
    urlField: "invitationUrl",
    loadingText: "Generating secure link...",
    alertText:
      "This link expires in 96 hours and works exactly like the email invitation. Share it securely with the user through your preferred communication method.",
    copyMessage: "Invitation link copied to clipboard!",
  },
  passwordReset: {
    title: "Generate Password Reset Link",
    buttonText: "Generate Link",
    fieldLabel: "Password Reset Link",
    icon: KeyIcon,
    mutation: GENERATE_PASSWORD_RESET_LINK_MUTATION,
    urlField: "resetPasswordUrl",
    loadingText: "Generating secure reset link...",
    alertText:
      "This link expires in 96 hours and allows the user to reset their password. Share it securely with the user through your preferred communication method.",
    copyMessage: "Password reset link copied to clipboard!",
  },
};

const LinkGenerator = ({ type, userId, userEmail, open, onClose }) => {
  const config = LINK_CONFIGS[type];
  const linkLabel = type === "invitation" ? "invitation" : "password reset";
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [generateLink] = useMutation(config.mutation);
  const { notifySuccess, notifyError } = useNotification();

  const Icon = config.icon;

  const handleGenerateLink = async () => {
    setLoading(true);
    setError("");

    try {
      await generateLink({
        variables: { userId },
        onSuccess: data => {
          setUrl(data[config.urlField]);
          setLoading(false);
        },
        onFailure: errors => {
          const errorMessage =
            errors?.submit ||
            Object.values(errors || {})[0] ||
            `Failed to generate ${linkLabel} link`;
          setError(errorMessage);
          notifyError(errorMessage);
          setLoading(false);
        },
      });
    } catch (err) {
      const errorMessage = `Failed to generate ${linkLabel} link`;
      setError(errorMessage);
      notifyError(errorMessage);
      setLoading(false);
    }
  };

  const handleClose = () => {
    setUrl("");
    setError("");
    setLoading(false);
    onClose();
  };

  const handleCopySuccess = () => {
    notifySuccess(config.copyMessage);
  };

  return (
    <ActionModal
      open={open}
      onClose={handleClose}
      title={config.title}
      maxWidth={600}
      showClose={true}
    >
      <Box mt={2}>
        <Typography variant="body1" color="text.secondary" align="center" gutterBottom>
          Generate a secure {linkLabel} link for <strong>{userEmail}</strong>
        </Typography>

        {error && (
          <Box mt={2} mb={2}>
            <Typography variant="body2" color="error" align="center">
              {error}
            </Typography>
          </Box>
        )}

        {!url && !loading && (
          <Box mt={4} display="flex" justifyContent="center">
            <Button
              variant="contained"
              color="primary"
              startIcon={<Icon />}
              onClick={handleGenerateLink}
              size="large"
              sx={{ minWidth: 200 }}
            >
              {config.buttonText}
            </Button>
          </Box>
        )}

        {loading && (
          <Box mt={4} display="flex" justifyContent="center" alignItems="center">
            <CircularProgress size={24} />
            <Typography variant="body2" sx={{ ml: 2 }}>
              {config.loadingText}
            </Typography>
          </Box>
        )}

        {url && (
          <Box mt={3}>
            <Grid container spacing={2}>
              <Grid size={12}>
                <TextField
                  label={config.fieldLabel}
                  value={url}
                  variant="outlined"
                  fullWidth
                  multiline
                  rows={3}
                  InputProps={{
                    readOnly: true,
                  }}
                />
              </Grid>
              <Grid size={12}>
                <Box display="flex" justifyContent="center">
                  <CopyToClipboard text={url} onCopy={handleCopySuccess}>
                    <Button variant="contained" color="primary" size="large">
                      Copy Link
                    </Button>
                  </CopyToClipboard>
                </Box>
              </Grid>
            </Grid>

            <Box mt={3} p={2} sx={{ backgroundColor: "#e3f2fd", borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Important:</strong> {config.alertText}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    </ActionModal>
  );
};

export default LinkGenerator;
